import type {CSSProperties, ReactNode} from "react";
import {useState, Suspense} from "react";
import type {SpringValue} from "react-spring";
import {animated} from "react-spring";
import type {colorThemes} from "@cdx/ds/css/themes/themes.css";
import {Col, css} from "@cdx/ds";
import {useDSPositionWithNode} from "@cdx/ds/hooks/position-hooks";
import {useMeasureWithNode} from "../hooks/useMeasure";
import {useReveal, springConfigs} from "../hooks/useReveal";
import Portal from "../Portal";
import {RawSpinner} from "../xui/Spinner";
import type {ArrowProps} from "../xui/Arrow";
import Arrow from "../xui/Arrow";
import {useZIndex} from "./useZIndex";
import {useFitOnScreen} from "./FitOnScreen";
import {overlayStyles} from "./overlay.css";
import type {AnchorAlignment, OverlayPlacement, OverlayRect} from "./types";

export type {OverlayPlacement} from "./types";

type PresenceProps = {value: SpringValue<number>};

export type OverlayRenderProps = {
  anchorZIndex: number;
  presenceProps?: PresenceProps;
  isOpen?: boolean;
  anchorPosition: OverlayRect;
  style: CSSProperties;
  arrowOffsetLeft: string | number | undefined;
  arrowOffsetTop: string | number | undefined;
  placement: OverlayPlacement;
  setNode: (node: HTMLElement | null) => void;
};

type OverlayPlacerProps = {
  node: HTMLElement | null | undefined;
  isOpen?: boolean;
  presenceProps?: PresenceProps;
  backdrop?: boolean;
  placement?: OverlayPlacement;
  forcePlacement?: boolean;
  preventPlacement?: OverlayPlacement;
  viewportMargin?: number;
  distanceFromAnchor?: number;
  anchorAlignment?: AnchorAlignment;
  renderOverlay: (props: OverlayRenderProps) => ReactNode;
};

export const OverlayPlacer = ({
  node,
  isOpen,
  presenceProps,
  backdrop,
  placement: preferredPlacement,
  forcePlacement,
  preventPlacement,
  viewportMargin,
  distanceFromAnchor,
  anchorAlignment,
  renderOverlay,
}: OverlayPlacerProps) => {
  const anchorPosition = useDSPositionWithNode(node);
  const zIndex = useZIndex(node);
  const [overlayNode, setOverlayNode] = useState<HTMLElement | null>(null);
  const overlayDims = useMeasureWithNode(overlayNode);

  const fitProps = useFitOnScreen({
    preferredPlacement,
    anchorPosition,
    forcePlacement,
    preventPlacement,
    viewportMargin,
    distanceFromAnchor,
    anchorAlignment,
    overlayDims,
  });
  if (!anchorPosition || !fitProps || zIndex === null) return null;
  const {style, arrowOffsetLeft, arrowOffsetTop, placement} = fitProps;
  return (
    <>
      {backdrop && (
        <animated.div
          style={{zIndex: zIndex + 4, opacity: presenceProps?.value}}
          className={css(
            {
              display: "flex",
              flexDir: "column",
              position: "fixed",
              inset: "0",
            },
            overlayStyles.backdrop
          )}
        />
      )}
      {renderOverlay({
        anchorZIndex: zIndex + 5,
        presenceProps,
        isOpen,
        anchorPosition,
        style,
        arrowOffsetLeft,
        arrowOffsetTop,
        placement,
        setNode: setOverlayNode,
      })}
    </>
  );
};

type SpawnAnchoredOverlayWithNodeProps = Omit<OverlayPlacerProps, "presenceProps"> & {
  children?: ReactNode;
  springConfig?: {tension?: number; friction?: number};
};

export const SpawnAnchoredOverlayWithNode = ({
  children,
  isOpen,
  node,
  springConfig = springConfigs.quick,
  ...rest
}: SpawnAnchoredOverlayWithNodeProps) => {
  const reveal = useReveal(isOpen, {config: springConfig});

  return (
    <>
      {children}
      {reveal((props) => (
        <Portal>
          <OverlayPlacer node={node} presenceProps={props} isOpen={isOpen} {...rest} />
        </Portal>
      ))}
    </>
  );
};

type SpawnAnchoredOverlayProps = Omit<SpawnAnchoredOverlayWithNodeProps, "node" | "children"> & {
  children: ReactNode | ((setNode: (node: HTMLElement | null) => void) => ReactNode);
};

export const SpawnAnchoredOverlay = ({children, ...rest}: SpawnAnchoredOverlayProps) => {
  const [node, setNode] = useState<HTMLElement | null>(null);
  if (typeof children !== "function")
    return <div style={{width: 20, height: 20, background: "yellow"}} />;
  return (
    <SpawnAnchoredOverlayWithNode node={node} {...rest}>
      {children(setNode)}
    </SpawnAnchoredOverlayWithNode>
  );
};

type DefaultOverlayProps = Partial<OverlayRenderProps> & {
  children?: ReactNode;
  className?: string;
  bg?: any;
  noBg?: boolean;
  color?: any;
  onClick?: (e: React.MouseEvent) => void;
  close?: () => void;
  [key: string]: any;
};

export const DefaultOverlay = (props: DefaultOverlayProps) => {
  const {
    children,
    style,
    anchorZIndex,
    presenceProps,
    placement,
    setNode,
    className,
    bg,
    noBg,
    color,
    isOpen,
    arrowOffsetLeft,
    arrowOffsetTop,
    anchorPosition,
    onClick,
    close: _,
    ...restProps
  } = props;

  const xMod = placement === "left" ? 20 : placement === "right" ? -20 : 0;
  const yMod = placement === "top" ? 20 : placement === "bottom" ? -20 : 0;
  return (
    <animated.div
      className={css(
        {
          display: "flex",
          flexDir: "column",
          position: "absolute",
          rounded: 4,
          elevation: 100,
          minHeight: "0",
          ...(noBg ? {} : {bg: "true" as const, colorTheme: "white" as const}),
        },
        (overlayStyles as any).defaultOverlay,
        className
      )}
      style={{
        zIndex: anchorZIndex,
        opacity: presenceProps?.value,
        transform: presenceProps?.value.to(
          (val) => `translate3d(${xMod * (1 - val)}px, ${yMod * (1 - val)}px, 0)`
        ),
        pointerEvents: presenceProps?.value.to((val) => (val > 0.9 ? "initial" : "none")),
        ...style,
      }}
      ref={setNode}
      onClick={(e) => {
        e.stopPropagation();
        onClick?.(e);
      }}
      {...restProps}
    >
      <Suspense fallback={<RawSpinner />}>{children}</Suspense>
    </animated.div>
  );
};

const reversePlacement: Record<OverlayPlacement, OverlayPlacement> = {
  top: "bottom",
  bottom: "top",
  left: "right",
  right: "left",
};

type ArrowOverlayProps = Partial<OverlayRenderProps> & {
  children?: ReactNode;
  arrowSize?: ArrowProps["size"];
  contentStyle?: CSSProperties;
  containerStyle?: CSSProperties;
  className?: string;
  outerClassName?: string;
  close?: () => void;
  noPointerEvents?: boolean;
  onClick?: (e: React.MouseEvent) => void;
  allowMouseEventBubbling?: boolean;
  colorTheme?: keyof typeof colorThemes;
};

export const ArrowOverlay = (props: ArrowOverlayProps) => {
  const {
    children,
    arrowSize = "md",
    style: {overflow, ...restStyle} = {},
    anchorZIndex,
    presenceProps,
    placement,
    contentStyle,
    containerStyle: _0,
    setNode,
    className,
    outerClassName,
    isOpen: _2,
    arrowOffsetLeft,
    arrowOffsetTop,
    anchorPosition: _3,
    close: _4,
    noPointerEvents,
    onClick,
    allowMouseEventBubbling,
    colorTheme = "white",
    ...rest
  } = props;

  const xMod = placement === "left" ? 20 : placement === "right" ? -20 : 0;
  const yMod = placement === "top" ? 20 : placement === "bottom" ? -20 : 0;

  return (
    <animated.div
      className={css(
        {
          display: "flex",
          flexDir: "column",
          position: "absolute",
          minHeight: "0",
          colorTheme,
        },
        outerClassName
      )}
      style={{
        zIndex: anchorZIndex,
        opacity: presenceProps?.value,
        transform: presenceProps?.value.to(
          (val) => `translate3d(${xMod * (1 - val)}px, ${yMod * (1 - val)}px, 0)`
        ),
        pointerEvents: presenceProps?.value.to((val) =>
          val > 0.9 && !noPointerEvents ? "initial" : "none"
        ),
        ...restStyle,
      }}
      ref={setNode}
      onClick={(e) => {
        e.stopPropagation();
        onClick?.(e);
      }}
      {...(allowMouseEventBubbling
        ? {}
        : {
            onMouseDown: (e: React.MouseEvent) => e.stopPropagation(),
            onMouseUp: (e: React.MouseEvent) => e.stopPropagation(),
          })}
      {...rest}
    >
      <Suspense fallback={<RawSpinner />}>
        <Col
          elevation={200}
          bg
          color="secondary"
          className={className}
          style={{overflow, ...contentStyle}}
          minHeight="0"
          rounded={4}
          overflow="hidden"
        >
          {children}
        </Col>
        <Arrow
          size={arrowSize}
          pointTo={reversePlacement[placement as OverlayPlacement]}
          style={{
            top: arrowOffsetTop || undefined,
            left: arrowOffsetLeft || undefined,
          }}
        />
      </Suspense>
    </animated.div>
  );
};
